import React, { useState, useEffect, useRef } from 'react';

interface ScraperStats {
  startTime: string | null;
  citiesScraped: number;
  listingsFound: number;
  filteredCount: number;
  dealsFound: number;
  apiCallsMade: number;
  errors: number;
}

interface Progress {
  total: number;
  completed: number;
  inProgress: number;
  pending: number;
  errors: number;
  percentComplete: string;
  totalListings: number;
  totalFiltered: number;
  totalDeals: number;
  currentCity: string | null;
  currentState: string | null;
}

interface ScraperStatus {
  isRunning: boolean;
  currentCity: string | null;
  stats: ScraperStats;
  progress: Progress;
}

interface StateProgress {
  _id: string;
  total: number;
  completed: number;
  pending: number;
  inProgress: number;
  errors: number;
  listings: number;
  filtered: number;
  deals: number;
}

interface Deal {
  _id: string;
  fullAddress: string;
  city: string;
  state: string;
  price: number;
  zestimate: number;
  dealRatio: number;
  beds: number;
  baths: number;
  sqft: number;
  detailUrl: string;
}

interface Address {
  _id: string;
  fullAddress: string;
  city: string;
  state: string;
  price: number;
  zestimate: number;
  dealRatio: number;
  isDeal: boolean;
  beds: number;
  baths: number;
  sqft: number;
  detailUrl: string;
  scrapedAt: string;
}

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3015';

export default function ContinuousScraper() {
  const [status, setStatus] = useState<ScraperStatus | null>(null);
  const [stateProgress, setStateProgress] = useState<StateProgress[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [addressPage, setAddressPage] = useState(1);
  const [addressTotal, setAddressTotal] = useState(0);
  const [addressTotalPages, setAddressTotalPages] = useState(0);
  const [logs, setLogs] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'states' | 'deals' | 'addresses'>('dashboard');

  const eventSourceRef = useRef<EventSource | null>(null);
  const logsEndRef = useRef<HTMLDivElement>(null);

  // Fetch initial status
  const fetchStatus = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/zillow/continuous/status`);
      const data = await res.json();
      setStatus(data);
    } catch (err: any) {
      setError(err.message);
    }
  };

  // Fetch state progress
  const fetchStateProgress = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/zillow/continuous/progress-by-state`);
      const data = await res.json();
      setStateProgress(data.progress || []);
    } catch (err: any) {
      console.error('Error fetching state progress:', err);
    }
  };

  // Fetch deals
  const fetchDeals = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/zillow/continuous/deals?limit=100`);
      const data = await res.json();
      setDeals(data.deals || []);
    } catch (err: any) {
      console.error('Error fetching deals:', err);
    }
  };

  // Fetch all addresses
  const fetchAddresses = async (page = 1) => {
    try {
      const res = await fetch(`${API_BASE}/api/zillow/continuous/addresses?page=${page}&limit=50`);
      const data = await res.json();
      setAddresses(data.addresses || []);
      setAddressTotal(data.total || 0);
      setAddressTotalPages(data.totalPages || 0);
      setAddressPage(page);
    } catch (err: any) {
      console.error('Error fetching addresses:', err);
    }
  };

  // Connect to SSE stream
  const connectSSE = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const es = new EventSource(`${API_BASE}/api/zillow/continuous/stream`);

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'status') {
          setStatus(data);
        } else if (data.type === 'started') {
          addLog('Scraper started');
          fetchStatus();
        } else if (data.type === 'stopped') {
          addLog('Scraper stopped');
          fetchStatus();
        } else if (data.type === 'scraping') {
          addLog(`Scraping: ${data.city}, ${data.state} (${data.priceRange})`);
        } else if (data.type === 'city_complete') {
          addLog(`Completed: ${data.city}, ${data.state} - ${data.listings} listings, ${data.filtered} filtered, ${data.deals} deals`);
          fetchStatus();
          fetchDeals();
        } else if (data.type === 'cycle_complete') {
          addLog('Cycle complete! Restarting...');
        } else if (data.type === 'error') {
          addLog(`Error: ${data.city}, ${data.state} - ${data.error}`);
        }
      } catch (e) {
        console.error('Error parsing SSE data:', e);
      }
    };

    es.onerror = () => {
      console.error('SSE connection error');
      setTimeout(connectSSE, 5000);
    };

    eventSourceRef.current = es;
  };

  // Add log entry
  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev.slice(-99), `[${timestamp}] ${message}`]);
  };

  // Start scraper
  const handleStart = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/zillow/continuous/start`, {
        method: 'POST'
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.message);
      } else {
        addLog('Starting scraper...');
      }
      fetchStatus();
    } catch (err: any) {
      setError(err.message);
    }
    setLoading(false);
  };

  // Stop scraper
  const handleStop = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/zillow/continuous/stop`, {
        method: 'POST'
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.message);
      } else {
        addLog('Stopping scraper...');
      }
      fetchStatus();
    } catch (err: any) {
      setError(err.message);
    }
    setLoading(false);
  };

  // Reset cities
  const handleReset = async () => {
    if (!window.confirm('Reset all cities to pending? This will restart the scraping from the beginning.')) {
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/zillow/continuous/reset`, {
        method: 'POST'
      });
      const data = await res.json();
      addLog(data.message);
      fetchStatus();
      fetchStateProgress();
    } catch (err: any) {
      setError(err.message);
    }
    setLoading(false);
  };

  // Auto-scroll logs
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  // Initial fetch and SSE connection
  useEffect(() => {
    fetchStatus();
    fetchStateProgress();
    fetchDeals();
    connectSSE();

    // Refresh state progress periodically
    const interval = setInterval(() => {
      fetchStateProgress();
    }, 30000);

    return () => {
      clearInterval(interval);
      eventSourceRef.current?.close();
    };
  }, []);

  const progress = status?.progress;
  const stats = status?.stats;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Continuous City Scraper</h1>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Status Card */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className={`w-4 h-4 rounded-full ${status?.isRunning ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
            <span className="text-xl font-semibold">
              {status?.isRunning ? 'RUNNING' : 'STOPPED'}
            </span>
          </div>

          <div className="flex gap-2">
            {!status?.isRunning ? (
              <button
                onClick={handleStart}
                disabled={loading}
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-lg font-semibold disabled:opacity-50"
              >
                START
              </button>
            ) : (
              <button
                onClick={handleStop}
                disabled={loading}
                className="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-lg font-semibold disabled:opacity-50"
              >
                STOP
              </button>
            )}
            <button
              onClick={handleReset}
              disabled={loading || status?.isRunning}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg disabled:opacity-50"
            >
              Reset
            </button>
          </div>
        </div>

        {status?.currentCity && (
          <div className="text-lg text-blue-600 mb-4">
            Currently scraping: <strong>{status.currentCity}</strong>
          </div>
        )}

        {/* Progress Bar */}
        {progress && (
          <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>{progress.completed.toLocaleString()} / {progress.total.toLocaleString()} cities</span>
              <span>{progress.percentComplete}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div
                className="bg-blue-500 h-4 rounded-full transition-all duration-500"
                style={{ width: `${progress.percentComplete}%` }}
              />
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{progress?.completed.toLocaleString() || 0}</div>
            <div className="text-sm text-gray-600">Cities Scraped</div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{progress?.totalFiltered.toLocaleString() || 0}</div>
            <div className="text-sm text-gray-600">Filtered Addresses</div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">{progress?.totalDeals.toLocaleString() || 0}</div>
            <div className="text-sm text-gray-600">Deals Found</div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">{stats?.apiCallsMade.toLocaleString() || 0}</div>
            <div className="text-sm text-gray-600">API Calls Made</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setActiveTab('dashboard')}
          className={`px-4 py-2 rounded-lg ${activeTab === 'dashboard' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
        >
          Live Logs
        </button>
        <button
          onClick={() => { setActiveTab('states'); fetchStateProgress(); }}
          className={`px-4 py-2 rounded-lg ${activeTab === 'states' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
        >
          State Progress
        </button>
        <button
          onClick={() => { setActiveTab('deals'); fetchDeals(); }}
          className={`px-4 py-2 rounded-lg ${activeTab === 'deals' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
        >
          Deals ({progress?.totalDeals || 0})
        </button>
        <button
          onClick={() => { setActiveTab('addresses'); fetchAddresses(1); }}
          className={`px-4 py-2 rounded-lg ${activeTab === 'addresses' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
        >
          All Addresses
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'dashboard' && (
        <div className="bg-black text-green-400 rounded-lg p-4 h-96 overflow-y-auto font-mono text-sm">
          {logs.length === 0 ? (
            <div className="text-gray-500">No logs yet. Start the scraper to see activity.</div>
          ) : (
            logs.map((log, i) => (
              <div key={i} className="py-1">{log}</div>
            ))
          )}
          <div ref={logsEndRef} />
        </div>
      )}

      {activeTab === 'states' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left">State</th>
                <th className="px-4 py-3 text-right">Cities</th>
                <th className="px-4 py-3 text-right">Completed</th>
                <th className="px-4 py-3 text-right">Progress</th>
                <th className="px-4 py-3 text-right">Listings</th>
                <th className="px-4 py-3 text-right">Filtered</th>
                <th className="px-4 py-3 text-right">Deals</th>
              </tr>
            </thead>
            <tbody>
              {stateProgress.map((state) => (
                <tr key={state._id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">{state._id}</td>
                  <td className="px-4 py-3 text-right">{state.total}</td>
                  <td className="px-4 py-3 text-right">{state.completed}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-500 h-2 rounded-full"
                          style={{ width: `${(state.completed / state.total * 100).toFixed(0)}%` }}
                        />
                      </div>
                      <span className="text-sm">{(state.completed / state.total * 100).toFixed(0)}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">{state.listings.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right">{state.filtered.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-semibold text-orange-600">{state.deals}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'deals' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left">Address</th>
                <th className="px-4 py-3 text-right">Price</th>
                <th className="px-4 py-3 text-right">Zestimate</th>
                <th className="px-4 py-3 text-right">Ratio</th>
                <th className="px-4 py-3 text-right">Beds/Baths</th>
                <th className="px-4 py-3 text-right">Sqft</th>
                <th className="px-4 py-3 text-center">Link</th>
              </tr>
            </thead>
            <tbody>
              {deals.map((deal) => (
                <tr key={deal._id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-semibold">{deal.fullAddress}</div>
                    <div className="text-sm text-gray-500">{deal.city}, {deal.state}</div>
                  </td>
                  <td className="px-4 py-3 text-right">${deal.price?.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-green-600">${deal.zestimate?.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-bold text-orange-600">{deal.dealRatio}x</td>
                  <td className="px-4 py-3 text-right">{deal.beds}/{deal.baths}</td>
                  <td className="px-4 py-3 text-right">{deal.sqft?.toLocaleString()}</td>
                  <td className="px-4 py-3 text-center">
                    <a
                      href={deal.detailUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:underline"
                    >
                      View
                    </a>
                  </td>
                </tr>
              ))}
              {deals.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    No deals found yet. Start the scraper to find deals.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'addresses' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* Header with total count */}
          <div className="bg-gray-50 px-4 py-3 border-b flex justify-between items-center">
            <span className="font-semibold">
              Total Addresses: {addressTotal.toLocaleString()}
            </span>
            <span className="text-sm text-gray-500">
              Page {addressPage} of {addressTotalPages}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left">#</th>
                  <th className="px-4 py-3 text-left">Address</th>
                  <th className="px-4 py-3 text-right">Price</th>
                  <th className="px-4 py-3 text-right">Zestimate</th>
                  <th className="px-4 py-3 text-right">Ratio</th>
                  <th className="px-4 py-3 text-center">Deal</th>
                  <th className="px-4 py-3 text-left">Details</th>
                  <th className="px-4 py-3 text-center">Link</th>
                </tr>
              </thead>
              <tbody>
                {addresses.map((addr, index) => (
                  <tr
                    key={addr._id}
                    className={`border-t hover:bg-gray-50 ${addr.isDeal ? 'bg-purple-50' : ''}`}
                  >
                    <td className="px-4 py-3 text-gray-500">
                      {(addressPage - 1) * 50 + index + 1}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{addr.fullAddress}</div>
                      <div className="text-sm text-gray-500">{addr.city}, {addr.state}</div>
                    </td>
                    <td className="px-4 py-3 text-right">${addr.price?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-green-600">
                      {addr.zestimate ? `$${addr.zestimate.toLocaleString()}` : '—'}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold">
                      {addr.dealRatio ? `${addr.dealRatio}x` : '—'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {addr.isDeal ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          YES
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {addr.beds} bd / {addr.baths} ba / {addr.sqft?.toLocaleString() || '—'} sqft
                    </td>
                    <td className="px-4 py-3 text-center">
                      {addr.detailUrl && (
                        <a
                          href={addr.detailUrl.startsWith('http') ? addr.detailUrl : `https://www.zillow.com${addr.detailUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:underline"
                        >
                          View
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
                {addresses.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      No addresses found yet. Start the scraper to collect addresses.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {addressTotalPages > 1 && (
            <div className="bg-gray-50 px-4 py-3 border-t flex justify-center gap-2">
              <button
                onClick={() => fetchAddresses(1)}
                disabled={addressPage === 1}
                className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
              >
                First
              </button>
              <button
                onClick={() => fetchAddresses(addressPage - 1)}
                disabled={addressPage === 1}
                className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
              >
                Prev
              </button>
              <span className="px-4 py-1">
                {addressPage} / {addressTotalPages}
              </span>
              <button
                onClick={() => fetchAddresses(addressPage + 1)}
                disabled={addressPage === addressTotalPages}
                className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
              >
                Next
              </button>
              <button
                onClick={() => fetchAddresses(addressTotalPages)}
                disabled={addressPage === addressTotalPages}
                className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
              >
                Last
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
